// Book-a-fee one-click (issue #108): POST /api/finance/processor-fees.
// The Record action on the always-actionable pin is PERIOD-SCOPED — it carries only the
// processor identity. The server resolves the OWED period (the A-selector) itself and STAMPS
// the fee's date into that period, so the pin is recordable before a report exists and a
// mis-dated fee can never leave the arm stuck. The processor's owed fee difference (gross
// estimate − already-recorded non-voided fees) is booked as an ordinary Confirmed Expenditure;
// the returned row rides the ledger, delta export, and per-payee itemization like any manual
// entry. Recording changes the sort-inbox ledger, the report preflight, and finance health
// (a fee dated into a filed period flags it for amendment), so all three are invalidated.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  BookOwedPeriodProcessorFeeRequest,
  BookProcessorFeeResponse,
  ExpenditureDto,
} from '@signalvote/shared';
import { apiJson } from './apiFetch';
import { invalidateFeePinQueries } from './feePinInvalidation';

/** Typed error carrying the server's message for a failed booking. */
export class BookProcessorFeeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BookProcessorFeeError';
  }
}

/**
 * Book the one-click processor fee for the owed period. Returns the created expenditure so the
 * caller can surface a confirmation; the sort-inbox surfaces, the report preflight, and finance
 * health all refresh.
 */
export function useBookProcessorFee() {
  const queryClient = useQueryClient();
  return useMutation<ExpenditureDto, Error, BookOwedPeriodProcessorFeeRequest>({
    mutationFn: async ({ processorId }) => {
      const body = await apiJson<BookProcessorFeeResponse>('/api/finance/processor-fees', {
        method: 'POST',
        json: { processorId },
        error: (error) => new BookProcessorFeeError(error.error ?? 'Failed to book the processor fee'),
      });
      return body.expenditure;
    },
    // Recording and stepping aside refresh the SAME three surfaces (the shared fee-pin set).
    onSuccess: () => invalidateFeePinQueries(queryClient),
  });
}
